import { useMemo } from "react";
import { useSdk } from "../../contexts/services/hooks/use-services-context";
import type { ConversationFact } from "../../managers/conversation/facts";
import type {
    AgentModelChoice,
    AnswerFeedback,
    AssistantProject,
    AssistantUsage,
    ComponentData,
    ConversationContextSize,
    RestoredConversation,
} from "../../managers/conversation/types/conversation";

export type Conversation = {
    handleSingleMessage: (message: string) => Promise<ComponentData>;
    resetSingleMessageCache: () => void;
    stopTurn: () => void;
    sendAnswerFeedback: (
        turnId: string,
        feedback: AnswerFeedback,
    ) => Promise<boolean>;
    setAgentModel: (choice: AgentModelChoice) => void;
    getConversationId: () => string;
    openConversation: (id: string) => Promise<RestoredConversation | null>;
    hasConversationMemory: () => boolean;
    getContextSize: () => ConversationContextSize | undefined;
    getUsage: () => AssistantUsage | undefined;
    getFacts: () => ConversationFact[];
    forgetFact: (key: string) => void;
    canCompactConversation: () => boolean;
    compactConversation: () => Promise<{ summary: string } | null>;
    getCurrentProject: () => AssistantProject | undefined;
    startProjectConversation: (project: AssistantProject) => void;
};

export function useConversation(): Conversation {
    const { conversationManager } = useSdk();

    return useMemo<Conversation>(
        () => ({
            handleSingleMessage: (...args) =>
                conversationManager.handleSingleMessage(...args),
            resetSingleMessageCache: () =>
                conversationManager.resetSingleMessageCache(),
            stopTurn: () => conversationManager.stopTurn(),
            sendAnswerFeedback: (...args) =>
                conversationManager.sendAnswerFeedback(...args),
            setAgentModel: (...args) =>
                conversationManager.setAgentModel(...args),
            getConversationId: () => conversationManager.getConversationId(),
            openConversation: (...args) =>
                conversationManager.openConversation(...args),
            hasConversationMemory: () =>
                conversationManager.hasConversationMemory(),
            getContextSize: () => conversationManager.getContextSize(),
            getUsage: () => conversationManager.getUsage(),
            getFacts: () => conversationManager.getFacts(),
            forgetFact: (...args) => conversationManager.forgetFact(...args),
            canCompactConversation: () =>
                conversationManager.canCompactConversation(),
            compactConversation: () =>
                conversationManager.compactConversation(),
            getCurrentProject: () => conversationManager.getCurrentProject(),
            startProjectConversation: (...args) =>
                conversationManager.startProjectConversation(...args),
        }),
        [conversationManager],
    );
}
